import { useCallback, useEffect, useRef, useState } from "react";

/**
 * useColumnResize — small hook to manage resizable Excel-style columns.
 * Persists widths in localStorage keyed by `storageKey`.
 *
 * Usage:
 *   const { widths, startResize } = useColumnResize("xls-couples", defaultWidths);
 *   <th style={{ width: widths[i] }}>... <span className="resize-handle" onMouseDown={(e) => startResize(e, i)} /></th>
 */
export function useColumnResize(storageKey, defaultWidths) {
  const [widths, setWidths] = useState(() => {
    try {
      const raw = localStorage.getItem(storageKey);
      if (raw) {
        const parsed = JSON.parse(raw);
        if (Array.isArray(parsed) && parsed.length === defaultWidths.length) {
          return parsed;
        }
      }
    } catch (_e) { void _e; }
    return [...defaultWidths];
  });

  const draggingRef = useRef(null);

  // Persist
  useEffect(() => {
    try { localStorage.setItem(storageKey, JSON.stringify(widths)); } catch (_e) { void _e; }
  }, [widths, storageKey]);

  const startResize = useCallback((e, index) => {
    e.preventDefault();
    const startX = e.clientX;
    const startW = widths[index];
    draggingRef.current = { index, startX, startW };
    document.body.style.cursor = "col-resize";

    const onMove = (ev) => {
      const dx = ev.clientX - startX;
      setWidths((prev) => {
        const next = [...prev];
        next[index] = Math.max(40, startW + dx);
        return next;
      });
    };
    const onUp = () => {
      draggingRef.current = null;
      document.body.style.cursor = "";
      document.removeEventListener("mousemove", onMove);
      document.removeEventListener("mouseup", onUp);
    };
    document.addEventListener("mousemove", onMove);
    document.addEventListener("mouseup", onUp);
  }, [widths]);

  // Touch support for tablets
  const startResizeTouch = useCallback((e, index) => {
    if (!e.touches || !e.touches[0]) return;
    const startX = e.touches[0].clientX;
    const startW = widths[index];
    const onMove = (ev) => {
      const t = ev.touches[0];
      if (!t) return;
      const dx = t.clientX - startX;
      setWidths((prev) => {
        const next = [...prev];
        next[index] = Math.max(40, startW + dx);
        return next;
      });
    };
    const onEnd = () => {
      document.removeEventListener("touchmove", onMove);
      document.removeEventListener("touchend", onEnd);
    };
    document.addEventListener("touchmove", onMove, { passive: true });
    document.addEventListener("touchend", onEnd);
  }, [widths]);

  const resetWidths = useCallback(() => setWidths([...defaultWidths]), [defaultWidths]);

  return { widths, startResize, startResizeTouch, resetWidths };
}
